import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { CheckCircle2, ArrowLeft } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setDone(true);
    } catch (err) {
      // Still show success — backend never reveals if account exists
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2">
          <BrandLogo size={40} />
          <p className="font-heading text-lg font-bold">NestVillage Care Metrics</p>
        </div>
        {done ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-900" data-testid="forgot-success">
            <CheckCircle2 className="text-emerald-600 mb-2" />
            <h2 className="text-h3 font-bold">Check your email</h2>
            <p className="text-sm mt-2 leading-relaxed">
              If an active account matches <b>{email}</b>, we've sent a password reset link. The link expires in 30 minutes.
            </p>
            <p className="text-sm mt-3 text-emerald-800">Don't see it? Check your spam folder.</p>
            <Link to="/login" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:underline">
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h2 className="text-h1">Forgot your password?</h2>
            <p className="text-sm text-slate-500 mt-1.5">
              Enter the email associated with your account and we'll send you a link to reset your password.
            </p>
            <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="forgot-form">
              <div>
                <label className="text-small-caps">Email</label>
                <input
                  type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400"
                  data-testid="forgot-email-input"
                />
              </div>
              <button type="submit" disabled={submitting}
                className="w-full px-4 py-2.5 rounded-lg bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold disabled:opacity-60"
                data-testid="forgot-submit-btn">
                {submitting ? "Sending…" : "Send reset link"}
              </button>
            </form>
            <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
